import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { changePasswordInputSchema, type ChangePasswordInput } from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FloatingField } from '@/components/ui/floating-field';
import { useChangePassword } from '@/hooks/use-users';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const defaultValues: ChangePasswordInput = { current: '', next: '' };

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const change = useChangePassword();
  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordInputSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [open, form]);

  async function onSubmit(data: ChangePasswordInput) {
    try {
      await change.mutateAsync(data);
      toast.success('Senha alterada');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao alterar';
      toast.error(msg);
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Alterar senha">
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FloatingField
          label="Senha atual"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.current?.message}
          {...form.register('current')}
        />
        <FloatingField
          label="Nova senha (mín. 6 caracteres)"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.next?.message}
          {...form.register('next')}
        />

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium transition active:scale-[0.97] hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={change.isPending}
            className="flex flex-[2] items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-60"
          >
            <Check className="h-4 w-4" />
            {change.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}
